import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Trophy, X } from "lucide-react";
import { PianoKeyboard } from "@/components/medusa/PianoKeyboard";
import { grantXP, saveProgress } from "@/components/medusa/player";

const HIT_WINDOW = 150; // ms
const PERFECT_WINDOW = 90;
const APPROACH_MS = 1800; // note travel time

// Song Performance Mode: cinematic falling notes over the real piano keyboard.
export function SongPerformance({ song, engine, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | countdown | playing | over
  const [count, setCount] = useState(3);
  const [hud, setHud] = useState({ score: 0, combo: 0, best: 0 });
  const [petrified, setPetrified] = useState([]);
  const [shatter, setShatter] = useState(false);
  const [result, setResult] = useState(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stRef = useRef(null);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const begin = () => {
    setCount(3);
    setPhase("countdown");
    let c = 3;
    const t = setInterval(() => {
      c -= 1;
      if (c <= 0) {
        clearInterval(t);
        startRun();
      } else setCount(c);
    }, 700);
  };

  const startRun = () => {
    engine?.current?.ensureCtx?.();
    stRef.current = {
      t0: performance.now(), hits: 0, perfects: 0, misses: 0,
      score: 0, combo: 0, best: 0, over: false,
      judged: new Set(),
    };
    setPhase("playing");
    rafRef.current = requestAnimationFrame(draw);
  };

  const endRun = useCallback(async () => {
    const st = stRef.current;
    st.over = true;
    cancelAnimationFrame(rafRef.current);
    const total = song.chart.length;
    const accuracy = Math.round(((st.perfects + (st.hits - st.perfects) * 0.6) / total) * 100);
    const fullCombo = st.misses === 0;
    const xp = Math.round(song.xp * (accuracy / 100)) + (fullCombo ? 50 : 0);
    setResult({ accuracy, xp, fullCombo, score: st.score, best: st.best, misses: st.misses });
    setPhase("over");
    await grantXP("piano", xp, `Song performance: ${song.title}${fullCombo ? " (FULL COMBO)" : ""}`, song.song_id);
    await saveProgress("song", song.song_id, st.score, accuracy);
  }, [song]);

  const draw = useCallback(() => {
    const st = stRef.current;
    const canvas = canvasRef.current;
    if (!st || st.over || !canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width = canvas.offsetWidth;
    const h = canvas.height = canvas.offsetHeight;
    const now = performance.now() - st.t0;
    const laneW = w / 15;
    const hitY = h - 14;
    ctx.clearRect(0, 0, w, h);
    // lanes
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.03)" : "rgba(168,85,247,0.05)";
      ctx.fillRect(i * laneW, 0, laneW, h);
    }
    // hit line
    ctx.strokeStyle = "rgba(255,184,0,0.9)"; ctx.lineWidth = 2;
    ctx.shadowColor = "#FFB800"; ctx.shadowBlur = 10;
    ctx.beginPath(); ctx.moveTo(0, hitY); ctx.lineTo(w, hitY); ctx.stroke();
    ctx.shadowBlur = 0;
    // notes
    song.chart.forEach(([tSec, key], idx) => {
      if (st.judged.has(idx)) return;
      const tMs = tSec * 1000;
      const y = hitY - ((tMs - now) / APPROACH_MS) * hitY;
      if (now - tMs > HIT_WINDOW) { // missed
        st.judged.add(idx);
        st.misses += 1; st.combo = 0;
        setHud({ score: st.score, combo: 0, best: st.best });
        setPetrified((p) => [...p, key]);
        setTimeout(() => setPetrified((p) => p.filter((k) => k !== key)), 1100);
        return;
      }
      if (y < -20) return;
      const grad = ctx.createLinearGradient(0, y - 26, 0, y);
      grad.addColorStop(0, "rgba(168,85,247,0.25)");
      grad.addColorStop(1, "#A855F7");
      ctx.fillStyle = grad;
      ctx.shadowColor = "#A855F7"; ctx.shadowBlur = 12;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(key * laneW + 2, y - 24, laneW - 4, 24, 6); else ctx.rect(key * laneW + 2, y - 24, laneW - 4, 24);
      ctx.fill();
      ctx.shadowBlur = 0;
    });
    // song end: all judged and last note passed
    const lastT = song.chart[song.chart.length - 1][0] * 1000;
    if (now > lastT + 900) { endRun(); return; }
    rafRef.current = requestAnimationFrame(draw);
  }, [song, endRun]);

  const handlePlay = (index) => {
    const st = stRef.current;
    if (!st || st.over || phase !== "playing") return;
    const now = performance.now() - st.t0;
    let bestIdx = -1, bestDiff = Infinity;
    song.chart.forEach(([tSec, key], idx) => {
      if (key !== index || st.judged.has(idx)) return;
      const d = Math.abs(now - tSec * 1000);
      if (d < bestDiff) { bestDiff = d; bestIdx = idx; }
    });
    if (bestIdx >= 0 && bestDiff <= HIT_WINDOW) {
      st.judged.add(bestIdx);
      st.hits += 1;
      const perfect = bestDiff <= PERFECT_WINDOW;
      if (perfect) st.perfects += 1;
      st.combo += 1;
      st.best = Math.max(st.best, st.combo);
      const mult = 1 + Math.min(3, Math.floor(st.combo / 8));
      st.score += (perfect ? 100 : 60) * mult;
      setHud({ score: st.score, combo: st.combo, best: st.best });
      if (st.combo > 0 && st.combo % 8 === 0) { // petrification shatter — emerald-gold burst
        setShatter(true);
        setPetrified([]);
        setTimeout(() => setShatter(false), 700);
      }
    }
  };

  return (
    <div className="rounded-2xl border border-fuchsia-500/30 bg-black/60 p-5 sm:p-6" data-testid="song-performance">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-300">{song.difficulty} · {song.bpm} BPM</p>
          <p className="font-display text-white font-bold">{song.title} <span className="text-ice/40 font-normal text-xs">— {song.artist}</span></p>
        </div>
        <button onClick={onClose} aria-label="Close song" className="text-ice/40 hover:text-white transition-colors" data-testid="song-close"><X size={18} /></button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="song-hud">
        {[["Score", hud.score], ["Combo", `×${hud.combo}`], ["Best", `×${hud.best}`]].map(([l, v]) => (
          <div key={l} className="bg-black/50 border border-[#26262e] rounded-lg p-2 text-center">
            <p className="font-display text-white font-bold text-sm" data-testid={`song-hud-${l.toLowerCase()}`}>{v}</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-ice/45">{l}</p>
          </div>
        ))}
      </div>

      <div className="relative">
        <canvas ref={canvasRef} className="w-full h-56 sm:h-72 rounded-t-xl bg-black/40" data-testid="song-canvas" />
        {shatter && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid="song-shatter">
            <p className="font-display text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-yellow-300 to-emerald-300 animate-ping">
              SHATTERED ×{hud.combo}
            </p>
        </div>
        )}
        {phase === "idle" && (
          <div className="absolute inset-0 bg-black/70 rounded-t-xl flex flex-col items-center justify-center text-center px-6">
            <p className="text-xs text-ice/55 max-w-xs">Notes fall toward the gold line. Strike the matching piano key the instant they land. Misses petrify the keys — an 8-hit combo shatters the stone.</p>
            <button onClick={begin} className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-sm px-8 py-3.5 rounded-full active:scale-95 transition-all" data-testid="song-start">
              <Play size={15} /> Perform
            </button>
          </div>
        )}
        {phase === "countdown" && (
          <div className="absolute inset-0 bg-black/60 rounded-t-xl flex items-center justify-center">
            <p className="font-display text-6xl font-bold text-fuchsia-300 animate-ping" data-testid="song-countdown">{count}</p>
          </div>
        )}
      </div>

      <PianoKeyboard engine={engine} onPlay={handlePlay} petrifiedKeys={petrified} disabled={phase !== "playing"} compact />

      {phase === "over" && result && (
        <div className="mt-5 rounded-xl border border-yellow-500/40 bg-yellow-950/10 p-5 text-center" data-testid="song-result">
          <Trophy size={22} className="mx-auto text-yellow-300 mb-2" />
          <p className="font-display text-white font-bold text-lg">{result.score.toLocaleString()} pts · {result.accuracy}% accuracy</p>
          <p className="text-xs text-ice/60 mt-1">Best combo ×{result.best} · {result.misses} missed</p>
          {result.fullCombo && <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">Full Combo — +50 XP bonus</p>}
          <p className="text-xs text-emerald-300 mt-1.5">+{result.xp} XP earned</p>
        </div>
      )}
    </div>
  );
}
